/**
 * AudioManager
 *
 * Scene-level singleton managing a shared Web Audio AudioContext.
 * Syncs the AudioListener position/orientation to the active camera each frame.
 * Used by Synth (and future Audio) behaviors for spatial audio output.
 */

import { Scene } from "@babylonjs/core/scene";
import { Observer } from "@babylonjs/core/Misc/observable";

/**
 * Events that carry user activation, so a suspended AudioContext may resume
 * and an autoplay-blocked media element may play from inside their handler.
 * iOS Safari grants activation on touchend, not on a touch pointerdown.
 */
const UNLOCK_EVENTS = ["pointerdown", "touchend", "keydown"] as const;

export class AudioManager {
    private _ctx: AudioContext | null = null;
    private _scene: Scene;
    private _listenerObserver: Observer<Scene> | null = null;
    /** Where the gesture listeners live while audio is still locked. */
    private _unlockTarget: EventTarget | null = null;
    /** Media plays the autoplay policy refused, re-run once on the next gesture. */
    private _gestureRetries = new Set<() => void>();

    constructor(scene: Scene) {
        this._scene = scene;
    }

    /**
     * Get the shared AudioContext, creating it lazily on first access.
     * Attempts to resume if suspended (browser autoplay policy).
     */
    getContext(): AudioContext {
        if (!this._ctx) {
            this._ctx = new AudioContext();
            this._startListenerSync();
        }
        this.resume();
        return this._ctx;
    }

    /**
     * Resume the context if one exists and is not running. Browsers only
     * allow this during a user gesture, so callers acting on a user action
     * (e.g. a tap-driven play) call it synchronously. While the context stays
     * locked, the next pointerdown/touchend/keydown on the page retries.
     * Never creates a context: element-only playback does not need one.
     */
    resume(): void {
        const ctx = this._ctx;
        if (!ctx || ctx.state === "running" || ctx.state === "closed") return;
        this._installUnlock();
        ctx.resume().then(
            () => this._releaseUnlockIfDone(),
            () => { /* still locked: the next gesture retries */ },
        );
    }

    /**
     * Run `retry` once, inside the next user gesture. For media whose play()
     * the autoplay policy refused: playing from within the gesture handler is
     * what the browser allows. Returns a function that cancels the retry.
     */
    retryOnGesture(retry: () => void): () => void {
        this._gestureRetries.add(retry);
        this._installUnlock();
        return () => {
            this._gestureRetries.delete(retry);
            this._releaseUnlockIfDone();
        };
    }

    private _onGesture = (): void => {
        this.resume();
        const retries = [...this._gestureRetries];
        this._gestureRetries.clear();
        for (const retry of retries) retry();
        this._releaseUnlockIfDone();
    };

    private _installUnlock(): void {
        if (this._unlockTarget) return;
        // The canvas's own window, so a viewer inside an iframe hears gestures
        // made in that frame. Capture phase: page handlers that stop
        // propagation must not swallow the unlock.
        const canvas = this._scene.getEngine().getRenderingCanvas();
        const target = canvas?.ownerDocument?.defaultView ?? (typeof window !== "undefined" ? window : null);
        if (!target) return;
        for (const type of UNLOCK_EVENTS) {
            target.addEventListener(type, this._onGesture, { capture: true });
        }
        this._unlockTarget = target;
    }

    private _releaseUnlockIfDone(): void {
        const ctxLocked = !!this._ctx && this._ctx.state !== "running" && this._ctx.state !== "closed";
        if (ctxLocked || this._gestureRetries.size > 0) return;
        this._removeUnlock();
    }

    private _removeUnlock(): void {
        const target = this._unlockTarget;
        if (!target) return;
        for (const type of UNLOCK_EVENTS) {
            target.removeEventListener(type, this._onGesture, { capture: true });
        }
        this._unlockTarget = null;
    }

    /**
     * Get the audio destination node for connecting spatial audio graphs.
     */
    getDestination(): AudioNode {
        return this.getContext().destination;
    }

    /**
     * Sync AudioListener position and orientation to the active camera each frame.
     */
    private _startListenerSync(): void {
        if (this._listenerObserver) return;
        this._listenerObserver = this._scene.onBeforeRenderObservable.add(() => {
            if (!this._ctx) return;
            const camera = this._scene.activeCamera;
            if (!camera) return;

            const listener = this._ctx.listener;
            const pos = camera.position;
            const fwd = camera.getForwardRay().direction;
            // Camera up is always world Y in this engine
            const upX = 0, upY = 1, upZ = 0;

            // Use setters if available (modern browsers), fall back to deprecated properties
            if (listener.positionX) {
                listener.positionX.value = pos.x;
                listener.positionY.value = pos.y;
                listener.positionZ.value = pos.z;
                listener.forwardX.value = fwd.x;
                listener.forwardY.value = fwd.y;
                listener.forwardZ.value = fwd.z;
                listener.upX.value = upX;
                listener.upY.value = upY;
                listener.upZ.value = upZ;
            } else {
                // Fallback for older browsers
                listener.setPosition(pos.x, pos.y, pos.z);
                listener.setOrientation(fwd.x, fwd.y, fwd.z, upX, upY, upZ);
            }
        });
    }

    /**
     * Dispose of the AudioContext and stop listener sync.
     */
    dispose(): void {
        this._gestureRetries.clear();
        this._removeUnlock();
        if (this._listenerObserver) {
            this._scene.onBeforeRenderObservable.remove(this._listenerObserver);
            this._listenerObserver = null;
        }
        if (this._ctx) {
            this._ctx.close().catch(() => { /* ignore */ });
            this._ctx = null;
        }
    }
}
